import { lazy, Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { Toaster } from "sonner";

import { useTheme } from "@/lib/theme";
import { AuthCallbackPage } from "@/components/auth/AuthCallbackPage";
import { LoginPage } from "@/components/auth/LoginPage";
import { RequireAdmin } from "@/components/auth/RequireAdmin";
import { RequireAuth } from "@/components/auth/RequireAuth";
import { RequireFinance } from "@/components/auth/RequireFinance";
import { RequireOperatingRole } from "@/components/auth/RequireOperatingRole";
import { RequireProjectLead } from "@/components/auth/RequireProjectLead";
import { RequireSuperadmin } from "@/components/auth/RequireSuperadmin";
import { HomePlaceholder } from "@/components/HomePlaceholder";
import { AppShell } from "@/components/shell/AppShell";
import { PipelineRedirect } from "@/components/projects/PipelineRedirect";

// START-1: die Kunden-, Projekt- und Bundle-Seiten laden nach wie alle anderen
// Seiten. Sie hingen als letzte fest im Startbuendel — und mit ihnen der
// Projekt-Assistent samt Kalkulations-Vorlagen, die Arbeitsliste und das
// Kunden-Formular. Die Startseite braucht keine davon; wer sie oeffnet, laedt
// ihren Teil beim ersten Besuch (wie seit W11-C die Projektseite).
const CustomersListPage = lazy(() =>
  import("@/components/customers/CustomersListPage").then((m) => ({
    default: m.CustomersListPage,
  })),
);
const CustomerCreatePage = lazy(() =>
  import("@/components/customers/CustomerCreatePage").then((m) => ({
    default: m.CustomerCreatePage,
  })),
);
const CustomerDetailPage = lazy(() =>
  import("@/components/customers/CustomerDetailPage").then((m) => ({
    default: m.CustomerDetailPage,
  })),
);
const CustomerEditPage = lazy(() =>
  import("@/components/customers/CustomerEditPage").then((m) => ({
    default: m.CustomerEditPage,
  })),
);
const BundleDetailPage = lazy(() =>
  import("@/components/bundles/BundleDetailPage").then((m) => ({
    default: m.BundleDetailPage,
  })),
);
const ProjectsListPage = lazy(() =>
  import("@/components/projects/ProjectsListPage").then((m) => ({
    default: m.ProjectsListPage,
  })),
);
const ProjectWizard = lazy(() =>
  import("@/components/projects/ProjectWizard").then((m) => ({
    default: m.ProjectWizard,
  })),
);
const ProjectEditPage = lazy(() =>
  import("@/components/projects/ProjectEditPage").then((m) => ({
    default: m.ProjectEditPage,
  })),
);

const ProjectCalcPage = lazy(() =>
  import("@/components/projects/ProjectCalcPage").then((m) => ({ default: m.ProjectCalcPage })),
);
// OFF-5: «Offerte gestalten» — lazy wie die Kalkulation (Startbuendel-Grenze).
const OfferPruefblattPage = lazy(() =>
  import("@/components/projects/offer-design/OfferPruefblattPage").then((m) => ({
    default: m.OfferPruefblattPage,
  })),
);
const ProjectOfferDesignPage = lazy(() =>
  import("@/components/projects/offer-design/ProjectOfferDesignPage").then((m) => ({
    default: m.ProjectOfferDesignPage,
  })),
);
import { AuthProvider } from "@/lib/auth/AuthContext";
import { QueryProvider } from "@/lib/query/QueryProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ChunkErrorBoundary } from "@/components/ChunkErrorBoundary";

const StyleGuidePage = lazy(() =>
  import("@/components/styleguide/StyleGuidePage").then((m) => ({ default: m.StyleGuidePage })),
);

const TeamPage = lazy(() =>
  import("@/components/team/TeamPage").then((m) => ({ default: m.TeamPage })),
);

const ProductsPage = lazy(() =>
  import("@/components/products/ProductsPage").then((m) => ({
    default: m.ProductsPage,
  })),
);

const EarningsPage = lazy(() =>
  import("@/components/earnings/EarningsPage").then((m) => ({
    default: m.EarningsPage,
  })),
);

// G-8-Nebenbefund: /rechnungen hing als EINZIGE Finance-Seite eager im
// Initial-Bundle — lazy wie FinanzenPage (size-limit 250 kB).
const InvoicesListPage = lazy(() =>
  import("@/components/invoices/InvoicesListPage").then((m) => ({
    default: m.InvoicesListPage,
  })),
);

const FinanzenPage = lazy(() =>
  import("@/components/finance/FinanzenPage").then((m) => ({
    default: m.FinanzenPage,
  })),
);

// W11-C: Projekt-Detail lazy wie Finanzen — die neu geschnittene
// Seite (Phasen-Fokus, Sektions-Karten, Lage & Verlauf) wuchs sonst ueber das
// Entry-Budget; als Hot-Path-Route laedt sie ihren Chunk beim ersten Besuch.
const ProjectDetailPage = lazy(() =>
  import("@/components/projects/ProjectDetailPage").then((m) => ({
    default: m.ProjectDetailPage,
  })),
);

// H-4 — Hilfe & Onboarding: eigener lazy Chunk (statischer Text, gehoert nicht
// ins Initial-Bundle). Kein Route-Guard (alle Rollen).
const HelpPage = lazy(() =>
  import("@/components/help/HelpPage").then((m) => ({
    default: m.HelpPage,
  })),
);

// NEU-1 — «Was ist neu»: die volle Liste hinter dem Fenster, eigener lazy
// Chunk (das Initial-Bundle traegt nur den Gate + Menue-Eintrag). Kein Guard.
const WhatsNewPage = lazy(() =>
  import("@/components/releaseNotes/WhatsNewPage").then((m) => ({
    default: m.WhatsNewPage,
  })),
);

// MCP-1 — «Persönlicher Assistent» (Konto-Menue) und die Zustimmungs-Seite,
// die ein Assistent im Browser oeffnet. Eigener lazy Chunk, kein Guard.
const AssistentPage = lazy(() =>
  import("@/components/assistent/AssistentPage").then((m) => ({
    default: m.AssistentPage,
  })),
);
const AssistentVerbindenPage = lazy(() =>
  import("@/components/assistent/AssistentVerbindenPage").then((m) => ({
    default: m.AssistentVerbindenPage,
  })),
);

// FRAGEN-1 — Fragebogen an einen Menschen im Team: eigener lazy Chunk. Die
// Fragen selbst kommen vom Server (sie koennen einen Lohn nennen).
const FragebogenPage = lazy(() =>
  import("@/components/fragebogen/FragebogenPage").then((m) => ({
    default: m.FragebogenPage,
  })),
);
// NACHFASS-2 — die Seite hinter den Knoepfen der Nachfass-Mail: eigener Chunk.
const NachfassenPage = lazy(() =>
  import("@/components/nachfassen/NachfassenPage").then((m) => ({
    default: m.NachfassenPage,
  })),
);
// G-8 — Liquiditaets-Cockpit als eigener lazy Chunk (size-limit-Disziplin:
// SVG-Kurve + Dialog gehoeren nicht ins Initial-Bundle).
const LiquiditaetsCockpitPage = lazy(() =>
  import("@/components/finance/liquiditaet/LiquiditaetsCockpitPage").then(
    (m) => ({ default: m.LiquiditaetsCockpitPage }),
  ),
);

const ActivityPage = lazy(() =>
  import("@/components/activity/ActivityPage").then((m) => ({
    default: m.ActivityPage,
  })),
);
// LADEPARK-5: Ladepark-Rechner — lazy (Rechenkern + Diagramm laedt nur, wer ihn oeffnet).
const LadeparkPage = lazy(() =>
  import("@/components/ladepark/LadeparkPage").then((m) => ({
    default: m.LadeparkPage,
  })),
);

// LEAD-23: der Menuepunkt «Leads» — nachgeladen wie jede Seite (START-1).
const LeadsPage = lazy(() =>
  import("@/components/leads/LeadsPage").then((m) => ({
    default: m.LeadsPage,
  })),
);
const SettingsShell = lazy(() =>
  import("@/components/settings/SettingsShell").then((m) => ({
    default: m.SettingsShell,
  })),
);

const SystemShell = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({ default: m.SystemShell })),
);
const SystemCockpitTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({ default: m.SystemCockpitTab })),
);
const SystemOpsTriggersTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemOpsTriggersTab,
  })),
);
const SystemOpsAuditTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemOpsAuditTab,
  })),
);
const SystemOpsToolsTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemOpsToolsTab,
  })),
);
const SystemOpsFeedbackTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemOpsFeedbackTab,
  })),
);
const SystemConfigTokensTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemConfigTokensTab,
  })),
);
const SystemConfigAgbTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemConfigAgbTab,
  })),
);
const SystemConfigLeadSpamTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemConfigLeadSpamTab,
  })),
);
const SystemConfigIntegrationsTab = lazy(() =>
  import("@/components/system/SystemShell").then((m) => ({
    default: m.SystemConfigIntegrationsTab,
  })),
);
const PartnerPortalPage = lazy(() =>
  import("@/components/partners/PartnerPortalPage").then((m) => ({
    default: m.PartnerPortalPage,
  })),
);
// TAGESMAIL-1: meldet einen Klick aus der Morgenmail — nur angemeldet, also
// hinter RequireAuth. Nachgeladen und nur geladen, wenn die Adresse das
// Merkmal traegt (`von=tagesmail`): die Startdatei hat kaum Luft.
const TagesmailKlickMelderLazy = lazy(() =>
  import("@/components/auth/TagesmailKlickMelder").then((m) => ({
    default: m.TagesmailKlickMelder,
  })),
);

function TagesmailKlickMelder() {
  const { search } = useLocation();
  if (!search.includes("von=tagesmail")) return null;
  return (
    <Suspense fallback={null}>
      <TagesmailKlickMelderLazy />
    </Suspense>
  );
}

function ShellLayout() {
  return (
    <RequireAuth>
      <TagesmailKlickMelder />
      <AppShell>
        {/* POL-1: ChunkErrorBoundary um die lazy-Routen — ein Stale-Deploy-
            Ladefehler zeigt hier den Reload-Hinweis (Shell bleibt stehen),
            echte Render-Fehler laufen weiter an die aeussere ErrorBoundary. */}
        <ChunkErrorBoundary>
          <Outlet />
        </ChunkErrorBoundary>
      </AppShell>
    </RequireAuth>
  );
}

// Sonner-Toaster mit App-Theme-Bindung. Phase 3.15 (2026-05-05): zuvor war
// kein Toaster gemountet — `toast.success/error` aus Hooks (Send-Letter,
// Offer-Pdf-Download, Portal-Sessions, Folder-Suggestions) blieben unsichtbar.
// `theme="system"` wuerde die OS-Praeferenz lesen, nicht den App-Theme-State.
function AppToaster() {
  const choice = useTheme();
  const sonnerTheme: "light" | "dark" | "system" = choice;
  return (
    <Toaster position="bottom-right" closeButton theme={sonnerTheme} />
  );
}

function RouteFallback() {
  return (
    <div className="flex min-h-full items-center justify-center p-8">
      <span className="text-xs text-muted-foreground">Lädt …</span>
    </div>
  );
}

export function App() {
  return (
    <ErrorBoundary scope="app-root">
      <QueryProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppToaster />
            <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route element={<ShellLayout />}>
              <Route path="/" element={<HomePlaceholder />} />
              {/* H-4: Hilfe & Erste Schritte — alle Rollen, kein Guard. */}
              <Route
                path="/hilfe"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <HelpPage />
                  </Suspense>
                }
              />
              {/* MCP-1: persoenlicher Assistent — alle Rollen, kein Guard;
                  jede Person sieht nur ihre eigenen Verbindungen. */}
              <Route
                path="/assistent"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <AssistentPage />
                  </Suspense>
                }
              />
              <Route
                path="/assistent/verbinden"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <AssistentVerbindenPage />
                  </Suspense>
                }
              />
              {/* NEU-1: «Was ist neu» — alle Rollen, kein Guard. */}
              <Route
                path="/was-ist-neu"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <WhatsNewPage />
                  </Suspense>
                }
              />
              {/* FRAGEN-1: kein Rollen-Guard — wer lesen darf, entscheidet der
                  Server je Fragebogen (Empfaenger + Superadmin). */}
              <Route
                path="/fragen/:kennung"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <FragebogenPage />
                  </Suspense>
                }
              />
              {/* NACHFASS-2: die Knoepfe der Mail «Hast du nachgefasst?» fuehren
                  hierher. Kein Rollen-Guard — der Server entscheidet (RLS am
                  Projekt; Buchhaltung und Vertretung bestaetigen nicht). */}
              <Route
                path="/nachfassen/:projectId"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <NachfassenPage />
                  </Suspense>
                }
              />
              {/* Technische / nicht-business Routen — bleiben fuer den
                  UN-impersonierten Superadmin erreichbar (Styleguide,
                  /admin-Redirect, Aktivitaeten-Timeline). */}
              <Route
                path="/styleguide"
                element={
                  <RequireSuperadmin>
                    <Suspense fallback={<RouteFallback />}>
                      <StyleGuidePage />
                    </Suspense>
                  </RequireSuperadmin>
                }
              />
              <Route
                path="/admin"
                element={<Navigate to="/system" replace />}
              />
              {/* /aktivitaeten — kombinierte audit_log + user_interactions-
                  Timeline (Ops-/Audit-Sicht, NICHT in ADMIN_NAV). Bleibt fuer
                  den Superadmin erreichbar, daher ausserhalb des
                  Business-Gates. */}
              <Route
                path="/aktivitaeten"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <ActivityPage />
                  </Suspense>
                }
              />
              {/* Business-Route-Gruppe (ADMIN_NAV ohne Home + Detail-/Unter-
                  routen). RequireOperatingRole leitet den UN-impersonierten
                  Superadmin freundlich auf den Kommandostand um (Daniel-
                  Direktive 2026-07-12: „nichts business-maessiges"); sobald er
                  impersoniert (effektive Rolle admin/pl/buchhaltung), rendern
                  die Routen ganz normal. Die Inner-Guards (RequireFinance/
                  -Admin/-ProjectLead) bleiben unveraendert = Defense in Depth.
                  /bundles/:id = Projekt-Bundles (Projekt-Unterdomaene) → mit
                  gesperrt. */}
              <Route element={<RequireOperatingRole />}>
                <Route
                  path="/kunden"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <CustomersListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/kunden/neu"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <CustomerCreatePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/kunden/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <CustomerDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/kunden/:id/bearbeiten"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <CustomerEditPage />
                    </Suspense>
                  }
                />
                {/* HOME-6 (2026-06-30): /finanzen ist wieder eine echte
                    Deep-Dive-Seite (volle Finanzsicht), admin + buchhaltung
                    (RequireFinance). Sandros Home zeigt nur noch den schlanken
                    Fruehwarn-Streifen; Notification-Klicks aus rows/kpi.tsx +
                    rows/grok.tsx (verlinken auf /finanzen) landen jetzt korrekt
                    auf der vollen Sicht statt per Redirect auf der Home. */}
                <Route
                  path="/finanzen"
                  element={
                    <RequireFinance>
                      <Suspense fallback={<RouteFallback />}>
                        <FinanzenPage />
                      </Suspense>
                    </RequireFinance>
                  }
                />
                {/* G-8: Liquiditaets-Cockpit (13-Wochen-Vorschau). */}
                <Route
                  path="/finanzen/liquiditaet"
                  element={
                    <RequireFinance>
                      <Suspense fallback={<RouteFallback />}>
                        <LiquiditaetsCockpitPage />
                      </Suspense>
                    </RequireFinance>
                  }
                />
                <Route
                  path="/rechnungen"
                  element={
                    <RequireFinance>
                      <Suspense fallback={<RouteFallback />}>
                        <InvoicesListPage />
                      </Suspense>
                    </RequireFinance>
                  }
                />
                <Route
                  path="/projekte"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProjectsListPage />
                    </Suspense>
                  }
                />
                {/* NAV-3: die Pipeline-Seite ist aufgeloest — sie war dieselbe
                    Liste wie /projekte, anders sortiert. Lesezeichen und alte
                    Links landen in der Arbeitsliste, Reiter Verkauf, laengster
                    Stillstand zuerst (Muster /leads, W12-3). */}
                <Route path="/pipeline" element={<PipelineRedirect />} />
                <Route
                  path="/projekte/neu"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProjectWizard />
                    </Suspense>
                  }
                />
                <Route
                  path="/projekte/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProjectDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/bundles/:id"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <BundleDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/projekte/:id/bearbeiten"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProjectEditPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/projekte/:id/kalkulation"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProjectCalcPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/projekte/:id/offerte"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProjectOfferDesignPage />
                    </Suspense>
                  }
                />
                {/* OFF-19: das Pruefblatt einer Offerte — nur intern. */}
                <Route
                  path="/projekte/:id/offerte/pruefblatt/:offerId"
                  element={
                    <RequireProjectLead>
                      <Suspense fallback={<RouteFallback />}>
                        <OfferPruefblattPage />
                      </Suspense>
                    </RequireProjectLead>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <RequireAdmin>
                      <Suspense fallback={<RouteFallback />}>
                        <TeamPage />
                      </Suspense>
                    </RequireAdmin>
                  }
                />
                {/* PS-10: interne Preis-Sicht fuer ALLE Mitarbeiter — kein
                    RequireAdmin mehr. Bedienung (Regler/Edit/Override) gated
                    die Seite selbst + die admin-only Endpoints dahinter. */}
                <Route
                  path="/produkte"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <ProductsPage />
                    </Suspense>
                  }
                />
                {/* LADEPARK-5: Ladepark-Rechner — alle operativen Rollen, ohne
                    Leisten-Eintrag (ein siebtes Blatt fuer den PL ist Daniels
                    Entscheid); Einstieg ueber ⌘K. */}
                <Route
                  path="/ladepark"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <LadeparkPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/verdienst"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <EarningsPage />
                    </Suspense>
                  }
                />
                {/* LEAD-23 (Marvin 07.10.2026): «Leads» ist wieder ein eigener
                    Punkt im Hauptmenue — die Arbeitsliste aller offenen
                    Anfragen. W12-3 hatte /leads auf den Neu-Reiter der
                    Projektliste umgeleitet; alte Bookmarks landen jetzt auf
                    der Seite selbst. Der Guard bleibt: Buchhaltung hat im
                    Posteingang nichts zu suchen. */}
                <Route
                  path="/leads"
                  element={
                    <RequireProjectLead>
                      <Suspense fallback={<RouteFallback />}>
                        <LeadsPage />
                      </Suspense>
                    </RequireProjectLead>
                  }
                />
                {/* LEAD-26: EIN Durchgang zum Aufraeumen. Die alte Adresse
                    (Palette, Lesezeichen, Link der Projektliste) leitet um. */}
                <Route
                  path="/leads/aufraeumen"
                  element={<Navigate to="/leads?korb=aufraeumen" replace />}
                />
              </Route>
              <Route
                path="/einstellungen"
                element={
                  <RequireAdmin>
                    <Suspense fallback={<RouteFallback />}>
                      <SettingsShell />
                    </Suspense>
                  </RequireAdmin>
                }
              />
              {/* Phase 5.139: /partner-onboarding wurde ins konsolidierte
                  /partner-portal mit ?tab=wartend gefaltet. Redirect erhaelt
                  alte Bookmarks + Notification-Click-Targets aus 5.132. */}
              <Route
                path="/partner-onboarding"
                element={<Navigate to="/partner-portal?tab=wartend" replace />}
              />
              {/* 5.228: alte /system/config/partner-portal-Bookmarks landen
                  auf dem neuen Preise-Hauptmenue-Tab (5.223 ?tab=ek-preise
                  wird vom Panel-Resolver intern auf preise alias'd). Steht
                  ausserhalb des /system-RequireSuperadmin-Subtrees, damit
                  auch admin (Sandro) redirected wird statt auf / geworfen. */}
              <Route
                path="/system/config/partner-portal"
                element={<Navigate to="/partner-portal?tab=preise" replace />}
              />
              <Route
                path="/partner-portal"
                element={
                  <RequireAdmin>
                    <Suspense fallback={<RouteFallback />}>
                      <PartnerPortalPage />
                    </Suspense>
                  </RequireAdmin>
                }
              />
              <Route
                path="/system"
                element={
                  <RequireSuperadmin>
                    <Suspense fallback={<RouteFallback />}>
                      <SystemShell />
                    </Suspense>
                  </RequireSuperadmin>
                }
              >
                <Route
                  index
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemCockpitTab />
                    </Suspense>
                  }
                />
                <Route
                  path="cockpit"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemCockpitTab />
                    </Suspense>
                  }
                />
                <Route
                  path="ops"
                  element={<Navigate to="/system/ops/triggers" replace />}
                />
                <Route
                  path="ops/triggers"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemOpsTriggersTab />
                    </Suspense>
                  }
                />
                <Route
                  path="ops/audit"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemOpsAuditTab />
                    </Suspense>
                  }
                />
                <Route
                  path="ops/tools"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemOpsToolsTab />
                    </Suspense>
                  }
                />
                <Route
                  path="ops/feedback"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemOpsFeedbackTab />
                    </Suspense>
                  }
                />
                <Route
                  path="config"
                  element={<Navigate to="/system/config/tokens" replace />}
                />
                <Route
                  path="config/tokens"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemConfigTokensTab />
                    </Suspense>
                  }
                />
                <Route
                  path="config/agb"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemConfigAgbTab />
                    </Suspense>
                  }
                />
                <Route
                  path="config/lead-spam"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemConfigLeadSpamTab />
                    </Suspense>
                  }
                />
                <Route
                  path="config/integrations"
                  element={
                    <Suspense fallback={<RouteFallback />}>
                      <SystemConfigIntegrationsTab />
                    </Suspense>
                  }
                />
                {/* Backward-Compat: alte flache URLs auf die neue 3-Tab-
                    Hierarchie umleiten. Bookmarks + Notification-Deeplinks
                    + Settings-Manage-Links bleiben gueltig (5.180 Phase b). */}
                <Route
                  path="tokens"
                  element={<Navigate to="/system/config/tokens" replace />}
                />
                <Route
                  path="agb"
                  element={<Navigate to="/system/config/agb" replace />}
                />
                <Route
                  path="lead-spam"
                  element={<Navigate to="/system/config/lead-spam" replace />}
                />
                <Route
                  path="jobs"
                  element={<Navigate to="/system/ops/triggers" replace />}
                />
                <Route
                  path="sync"
                  element={<Navigate to="/system/ops/triggers" replace />}
                />
                <Route
                  path="selftests"
                  element={<Navigate to="/system/ops/triggers" replace />}
                />
                <Route
                  path="audit"
                  element={<Navigate to="/system/ops/audit" replace />}
                />
                <Route
                  path="logs"
                  element={<Navigate to="/system/ops/audit" replace />}
                />
                <Route
                  path="outbox"
                  element={<Navigate to="/system/ops/tools" replace />}
                />
                <Route
                  path="customer-extract"
                  element={<Navigate to="/system/ops/tools" replace />}
                />
                <Route
                  path="step-backfill"
                  element={<Navigate to="/system/ops/tools" replace />}
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </AuthProvider>
      </QueryProvider>
    </ErrorBoundary>
  );
}
